import { SessionEvent, SessionEventType } from "@spectree/shared";
import { cn } from "@/lib/utils";
import {
  Check,
  Play,
  AlertCircle,
  Zap,
  ChevronRight,
  FileCode2,
  ShieldCheck,
} from "lucide-react";

export interface SessionEventItemProps {
  event: SessionEvent;
  isLast?: boolean;
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

type EventStyle = {
  dot: string;
  line: string;
  icon: typeof Check;
  bg: string;
};

function getStyle(type: SessionEventType): EventStyle {
  switch (type) {
    case SessionEventType.SESSION_FEATURE_COMPLETED:
    case SessionEventType.SESSION_TASK_COMPLETED:
    case SessionEventType.SESSION_PHASE_COMPLETED:
      return {
        dot: "bg-emerald-500 shadow-emerald-500/30 shadow-[0_0_8px]",
        line: "bg-emerald-500/20",
        icon: Check,
        bg: "bg-emerald-500/[0.06]",
      };
    case SessionEventType.SESSION_FEATURE_STARTED:
    case SessionEventType.SESSION_TASK_STARTED:
      return {
        dot: "bg-sky-500 shadow-sky-500/30 shadow-[0_0_8px]",
        line: "bg-sky-500/20",
        icon: Play,
        bg: "bg-sky-500/[0.06]",
      };
    case SessionEventType.SESSION_PHASE_STARTED:
      return {
        dot: "bg-violet-500 shadow-violet-500/30 shadow-[0_0_8px]",
        line: "bg-violet-500/20",
        icon: Zap,
        bg: "bg-violet-500/[0.06]",
      };
    case SessionEventType.SESSION_ERROR:
      return {
        dot: "bg-red-500 shadow-red-500/30 shadow-[0_0_8px]",
        line: "bg-red-500/20",
        icon: AlertCircle,
        bg: "bg-red-500/[0.06]",
      };
    default:
      return {
        dot: "bg-neutral-500",
        line: "bg-neutral-500/20",
        icon: ChevronRight,
        bg: "bg-white/[0.02]",
      };
  }
}

function getLabel(event: SessionEvent): { primary: string; secondary?: string; meta?: string[] } {
  switch (event.eventType) {
    case SessionEventType.SESSION_STARTED:
      return {
        primary: "Session started",
        secondary: `${event.payload.totalFeatures} features · ${event.payload.totalTasks} tasks`,
      };
    case SessionEventType.SESSION_ENDED:
      return {
        primary: "Session ended",
        secondary: event.payload.summary || event.payload.status || "",
      };
    case SessionEventType.SESSION_PHASE_STARTED:
      return {
        primary: `Phase ${event.payload.phaseNumber} started`,
        secondary: `${event.payload.featureCount} feature${event.payload.featureCount > 1 ? "s" : ""}${event.payload.canParallelize ? " · parallel" : ""}`,
      };
    case SessionEventType.SESSION_PHASE_COMPLETED:
      return {
        primary: `Phase ${event.payload.phaseNumber} completed`,
        secondary: `${event.payload.featureCount} feature${event.payload.featureCount > 1 ? "s" : ""} done`,
      };
    case SessionEventType.SESSION_FEATURE_STARTED:
      return {
        primary: event.payload.title,
        secondary: `${event.payload.identifier} · ${event.payload.taskCount ?? 0} tasks`,
      };
    case SessionEventType.SESSION_FEATURE_COMPLETED: {
      const dur = event.payload.durationMs ? formatMs(event.payload.durationMs) : null;
      return {
        primary: event.payload.title,
        secondary: `${event.payload.identifier} · ${event.payload.completedTaskCount ?? 0}/${event.payload.taskCount ?? 0} tasks${dur ? ` · ${dur}` : ""}`,
      };
    }
    case SessionEventType.SESSION_TASK_STARTED:
      return {
        primary: event.payload.title,
        secondary: event.payload.identifier,
      };
    case SessionEventType.SESSION_TASK_COMPLETED: {
      const dur = event.payload.durationMs ? formatMs(event.payload.durationMs) : null;
      const files = event.payload.filesModified;
      const checks = event.payload.totalValidationChecks;
      const meta: string[] = [];
      if (dur) meta.push(dur);
      if (files?.length) meta.push(`${files.length} file${files.length > 1 ? "s" : ""}`);
      if (checks) meta.push(`${event.payload.validationChecksPassed}/${checks} checks`);
      return {
        primary: event.payload.title,
        secondary: event.payload.identifier,
        meta,
      };
    }
    case SessionEventType.SESSION_TASK_PROGRESS:
      return {
        primary: event.payload.message,
        secondary: event.payload.identifier,
      };
    case SessionEventType.SESSION_ERROR:
      return {
        primary: event.payload.errorMessage,
        secondary: event.payload.errorCode,
      };
    default:
      return { primary: (event as SessionEvent).eventType };
  }
}

function getTypeTag(type: SessionEventType): string {
  switch (type) {
    case SessionEventType.SESSION_STARTED:
    case SessionEventType.SESSION_ENDED:
      return "lifecycle";
    case SessionEventType.SESSION_PHASE_STARTED:
    case SessionEventType.SESSION_PHASE_COMPLETED:
      return "phase";
    case SessionEventType.SESSION_FEATURE_STARTED:
    case SessionEventType.SESSION_FEATURE_COMPLETED:
      return "feature";
    case SessionEventType.SESSION_TASK_STARTED:
    case SessionEventType.SESSION_TASK_COMPLETED:
    case SessionEventType.SESSION_TASK_PROGRESS:
      return "task";
    case SessionEventType.SESSION_ERROR:
      return "error";
    default:
      return "";
  }
}

export function SessionEventItem({ event, isLast }: SessionEventItemProps) {
  const style = getStyle(event.eventType);
  const { primary, secondary, meta } = getLabel(event);
  const tag = getTypeTag(event.eventType);
  const isCompleted =
    event.eventType === SessionEventType.SESSION_FEATURE_COMPLETED ||
    event.eventType === SessionEventType.SESSION_TASK_COMPLETED ||
    event.eventType === SessionEventType.SESSION_PHASE_COMPLETED;
  const isPhase =
    event.eventType === SessionEventType.SESSION_PHASE_STARTED ||
    event.eventType === SessionEventType.SESSION_PHASE_COMPLETED;
  const isTask =
    event.eventType === SessionEventType.SESSION_TASK_STARTED ||
    event.eventType === SessionEventType.SESSION_TASK_COMPLETED;
  const taskCompleted = event.eventType === SessionEventType.SESSION_TASK_COMPLETED;

  return (
    <div className="flex gap-3 group">
      {/* Timeline column */}
      <div className="flex flex-col items-center w-5 shrink-0 pt-1">
        <div className={cn("h-2.5 w-2.5 rounded-full shrink-0 mt-1", style.dot)} />
        {!isLast && <div className={cn("w-px flex-1 mt-1", style.line)} />}
      </div>

      {/* Content */}
      <div
        className={cn(
          "flex-1 min-w-0 rounded-lg px-3 py-2 mb-1 transition-colors",
          isPhase ? style.bg : "hover:bg-white/[0.02]",
        )}
      >
        {/* Top row: tag + time */}
        <div className="flex items-center gap-2 mb-0.5">
          <span
            className={cn(
              "text-xs font-medium uppercase tracking-widest",
              isCompleted ? "text-emerald-400/80" : "text-muted-foreground",
            )}
          >
            {tag}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums font-mono">
            {formatTime(event.timestamp)}
          </span>
        </div>

        {/* Primary text */}
        <p className={cn(
          "text-[13px] leading-snug",
          isPhase ? "font-semibold" : "font-medium",
        )}>
          {primary}
        </p>

        {/* Secondary text */}
        {secondary && (
          <p className="text-xs text-muted-foreground mt-0.5">{secondary}</p>
        )}

        {/* Meta pills (files, validations, duration) */}
        {meta && meta.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {meta.map((m, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 bg-white/[0.04] text-muted-foreground border border-white/[0.06]"
              >
                {i === 1 && isTask && <FileCode2 className="h-2.5 w-2.5" />}
                {i === 2 && taskCompleted && <ShieldCheck className="h-2.5 w-2.5" />}
                {m}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
